import { z } from 'zod';

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().url(),
  VITE_API_TIMEOUT: z.string().transform(Number),
});

const env = envSchema.parse(import.meta.env);

export const API_BASE_URL = env.VITE_API_BASE_URL;
export const API_TIMEOUT = env.VITE_API_TIMEOUT;
export const IMAGE_BASE_URL = 'https://uploads.jumuiyaenergy.com/images/';
export const PLACEHOLDER_IMAGE = 'https://economia3.com/wp-content/themes/economia3/inc/component/img/no-image.png';

export const API_ENDPOINTS = {
  // Base URLs
  BASE_URL: API_BASE_URL,
  IMAGE_BASE_URL,
  PAYMENT_URL: 'https://vesencomputing.com/hj/lp_api.php',

  // Authentication endpoints
  LOGIN: `${API_BASE_URL}/api_login.php`,
  REGISTER: `${API_BASE_URL}/api_register.php`,
  RESET_PASSWORD: `${API_BASE_URL}/api_reset_password.php`,
  UPDATE_PASSWORD: `${API_BASE_URL}/api_update_password.php`,
  VERIFY_OTP: `${API_BASE_URL}/api_verify_code.php`,
  REQUEST_OTP: `${API_BASE_URL}/api_send_verification.php`,
  LOGOUT: `${API_BASE_URL}/api_logout.php`,
  CHECK_SESSION: `${API_BASE_URL}/api_check_session.php`,

  // Product endpoints
  GET_PRODUCTS: `${API_BASE_URL}/api_products.php`,
  GET_PRODUCT: `${API_BASE_URL}/api_single_product.php`,
  GET_PRODUCTS_BY_BRAND: `${API_BASE_URL}/api_products_by_brand.php`,
  GET_PRODUCTS_BY_VENDOR: `${API_BASE_URL}/api_products_by_retailer.php`,
  GET_PRODUCTS_BY_SUBCATEGORY: `${API_BASE_URL}/api_products_by_subcategory.php`,
  GET_SIMILAR_PRODUCTS: `${API_BASE_URL}/api_related_products.php`,
  GET_PRODUCTS_BY_CATEGORY: `${API_BASE_URL}/api_products_by_category.php`,
  GET_PRODUCTS_ON_OFFER: `${API_BASE_URL}/api_product_offers.php`,
  GET_NEW_PRODUCTS: `${API_BASE_URL}/api_new_products.php`,
  CHECK_PRODUCT_STOCK: `${API_BASE_URL}/api_check_product_in_stock.php`,

  // Category endpoints
  GET_CATEGORIES: `${API_BASE_URL}/api_categories.php`,
  GET_CATEGORY: `${API_BASE_URL}/api_single_categories.php`,

  // Subcategory endpoints
  GET_SUBCATEGORIES: `${API_BASE_URL}/api_subcategories.php`,
  GET_SUBCATEGORY: `${API_BASE_URL}/api_single_subcategory.php`,
  GET_SUBCATEGORIES_BY_CATEGORY: `${API_BASE_URL}/api_subcategory_by_category.php`,

  // Brand endpoints
  GET_BRANDS: `${API_BASE_URL}/api_brands.php`,
  GET_BRAND: `${API_BASE_URL}/api_single_brands.php`,

  // Address endpoints
  ADD_ADDRESS: `${API_BASE_URL}/api_add_address.php`,
  GET_ADDRESSES: `${API_BASE_URL}/api_address.php`,
  EDIT_ADDRESS: `${API_BASE_URL}/api_edit_address.php`,
  DELETE_ADDRESS: `${API_BASE_URL}/api_delete_address.php`,

  // Order endpoints
  CREATE_ORDER: `${API_BASE_URL}/api_create_order.php`,
  GET_ORDER_HISTORY: `${API_BASE_URL}/api_order_history.php`,
  GET_USER_ORDERS: `${API_BASE_URL}/api_get_orders.php`,
  GET_ORDER_DETAILS: `${API_BASE_URL}/api_get_order_details.php`,
  GET_ORDER_STATUS: `${API_BASE_URL}/api_order_status.php`,

  // Vendor endpoints
  GET_VENDORS: `${API_BASE_URL}/api_get_retailers.php`,

  // Location endpoints
  GET_LOCATION: `${API_BASE_URL}/api_get_location.php`,

  // Variation endpoints
  GET_VARIATIONS: `${API_BASE_URL}/api_variations_by_subcategory.php`,
} as const;

export const fetchWithTimeout = async (
  url: string,
  options: RequestInit & { timeout?: number } = {}
): Promise<Response> => {
  const { timeout = API_TIMEOUT } = options;

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'Origin': window.location.origin,
        ...options.headers,
      },
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
    }
    throw error;
  }
};

// Helper function to get image URL
export const getImageUrl = (imagePath: string) => {
  if (!imagePath) return PLACEHOLDER_IMAGE;
  if (imagePath.startsWith('http')) return imagePath;
  return `${IMAGE_BASE_URL}${imagePath}`;
};